import {ApolloError} from '@apollo/client'
import React, {useMemo} from 'react'
import {generatePath} from 'react-router'
import {Link as RouterLink} from 'react-router-dom'
import {CompartmentLink} from '../../common/router_links/CompartmentLink'
import {UserLink} from '../../common/router_links/UserLink'
import {Routes} from '../../common/Routes'
import {Rental} from '../../common/types/rental'
import {toDisplayDateTime} from '../../common/utils/dateUtils'
import {resolveBoxDisplayName} from '../../common/utils/resolveBoxDisplayName'
import {resolveProductStatusDisplayName} from '../../common/utils/resolveProductStatusDisplayName'
import {Box} from '../../webui/Box'
import {ShoppingCartIcon} from '../../webui/icons/ShoppingCartIcon'
import {Link} from '../../webui/Link'
import {LoadingSpinnerContainer} from '../../webui/progress/LoadingSpinnerContainer'
import {Rating} from '../../webui/Rating'
import {SortablePaginatedTable} from '../../webui/table/sortable-paginated-table/SortablePaginatedTable'
import {TableHeadCell} from '../../webui/table/sortable-paginated-table/TableHeadCell'
import {CopyRentalButton} from './CopyRentalButton'
import {PostBoxActionButton} from './PostBoxActionButton'
import {AllRentalData} from './types'
import {rentalComparator} from './utils/allRentalDataUtils'
import {resolvePaymentStatus} from './utils/resolvePaymentStatus'
import {contractTypeLabel} from '../../common/domain/contractType'
import {useAllBoxesLite} from '../box/gql/useAllBoxesLite'
import {GQL_ContractType} from '../../generated/graphql'

interface Props {
  error?: ApolloError
  hasReachedPenultimatePage?: () => void
  loading?: boolean
  rentals: Rental[]
  searchEnabled?: boolean
  title?: string

  // When true, shows a "Contract type" column and joins via useAllBoxesLite().
  // Default false to avoid triggering heavy box queries in pages that don't need it.
  showContractType?: boolean
}

const createTableHeadCells = (showContractType: boolean): TableHeadCell<AllRentalData>[] => {
  const cells: TableHeadCell<AllRentalData>[] = [
    {
      id: 'rentStart',
      label: 'Started',
      render: (value) => toDisplayDateTime(value['rentStart']),
      maxWidth: 120,
    },
    {
      id: 'commentsCount',
      label: 'Comments',
      render: (row) => row.commentsCount ?? 0,
      maxWidth: 60,
    },
    {
      id: 'box',
      label: 'Box',
      maxWidth: 160,
      render: (row) => (
        <Link component={RouterLink} to={generatePath(Routes.BOX, {id: row['box'].id})}>
          {resolveBoxDisplayName(row['box'])}
        </Link>
      ),
    },
  ]

  if (showContractType) {
    cells.push({
      id: 'contractType',
      label: 'Contract type',
      maxWidth: 140,
      render: (row) => <Box>{contractTypeLabel(row.contractType ?? null)}</Box>,
    })
  }

  cells.push(
    {
      id: 'compartment',
      label: 'Comp.',
      render: (row) => <CompartmentLink boxId={row.box.id} compartmentId={row.compartment.id} linkText={row.compartment.name ?? ''} />,
    },
    {
      id: 'product',
      label: 'Product',
      maxWidth: 120,
      render: (value) => (
        <Link component={RouterLink} to={generatePath(Routes.PRODUCT, {id: value['product'].id})}>
          <Box display={'flex'} justifyContent={'space-between'}>
            {value['product'].name}
            {value['product'].rentalType === 'buy' && (
              <Box ml={1} display={'flex'} alignItems={'center'}>
                <ShoppingCartIcon />
              </Box>
            )}
          </Box>
        </Link>
      ),
    },
    {
      id: 'rentBy',
      label: 'Rented by',
      render: (row) => <UserLink userId={row.rentBy.id} linkText={row['rentBy'].email || 'Anonymized user'} />,
    },
    {
      id: 'rentTime',
      label: 'Duration',
      render: (value) => value['rentTime'],
    },
    {
      id: 'rentEnd',
      label: 'Ended',
      maxWidth: 120,
      render: (value) => toDisplayDateTime(value['rentEnd']),
    },
    {
      id: 'productStatus',
      label: 'Product status',
      render: (value) => (value['productStatus'] ? resolveProductStatusDisplayName(value['productStatus']) : ''),
    },
    {
      id: 'productStatusText',
      label: 'Product status text',
      render: (value) => value['productStatusText'],
      maxWidth: 200,
    },
    {
      id: 'numberOfSuccessfulOpenings',
      label: 'Successful openings',
      render: (value) => value['numberOfSuccessfulOpenings'],
    },
    {
      id: 'numberOfFailedOpenings',
      label: 'Failed openings',
      render: (value) => value['numberOfFailedOpenings'],
    },
    {
      id: 'reviewRating',
      label: 'Rating',
      render: (value) => (value.review?.rating ? <Rating readOnly={true} value={value.review.rating} /> : '-'),
    },
    {
      id: 'reviewFreetext',
      label: 'Feedback',
      maxWidth: 160,
      render: (value) => value.reviewFreetext ?? '-',
    },
    {
      id: 'postBoxAction',
      label: 'N',
      render: (value) => <PostBoxActionButton rental={value} />,
      disableRowLink: true,
    },
    {
      id: 'paymentChargeInformation',
      label: 'Payment status',
      maxWidth: 160,
      render: (value) => resolvePaymentStatus(value['paymentChargeInformation']) ?? '',
    },
    {
      id: 'aborted',
      label: 'Aborted?',
      maxWidth: 120,
      render: (value) => (value['aborted'] ? 'YES' : ''),
    },
    {
      id: 'copyRental',
      label: '',
      render: (value) => <CopyRentalButton rental={value} />,
      disableRowLink: true,
    },
  )

  return cells
}

const INITIAL_ROWS_PER_PAGE = 20

export const RentalsTable: React.FC<Props> = ({
  error,
  hasReachedPenultimatePage,
  loading,
  rentals,
  searchEnabled,
  title,
  showContractType = false,
}) => {
  const {data: boxesLite} = useAllBoxesLite({skip: !showContractType})

  const boxIdToContractType = useMemo(() => {
    if (!showContractType) return new Map<string, GQL_ContractType | null>()

    const map = new Map<string, GQL_ContractType | null>()
    for (const b of boxesLite ?? []) {
      map.set(b.id, b.contractType ?? null)
    }
    return map
  }, [boxesLite, showContractType])

  const rows: AllRentalData[] = useMemo(() => {
    return rentals.map((rental) => {
      const commentsCount = (rental as any).internalComments?.length ?? ''
      return {
        id: rental.id,
        aborted: rental.aborted,
        box: rental.box,
        contractType: showContractType ? (boxIdToContractType.get(rental.box.id) ?? null) : null,
        compartment: rental.compartment,
        copyRental: true,
        postBoxAction: true,
        maxRentTimeInMinutes: rental.maxRentTimeInMinutes,
        paymentChargeInformation: rental.paymentChargeInformation,
        product: rental.product,
        productStatus: rental.productStatus,
        productStatusText: rental.productStatusText,
        numberOfFailedOpenings: rental.numberOfFailedOpenings,
        numberOfSuccessfulOpenings: rental.numberOfSuccessfulOpenings,
        rentBy: rental.rentBy,
        rentStartBy: rental.rentStartBy,
        rentEndBy: rental.rentEndBy,
        rentStart: rental.rentStart,
        rentEnd: rental.rentEnd,
        rentTime: rental.rentTime,
        review: rental.review,
        reviewRating: rental.review?.rating,
        reviewFreetext: rental.review?.freeText,
        commentsCount,
      }
    })
  }, [rentals, boxIdToContractType, showContractType])

  const tableHeadCells = useMemo(() => createTableHeadCells(showContractType), [showContractType])

  if (loading) {
    return <LoadingSpinnerContainer />
  }
  if (error) {
    return <div>{error.message}</div>
  }

  return (
    <SortablePaginatedTable
      getComparator={rentalComparator}
      hasReachedPenultimatePage={hasReachedPenultimatePage}
      title={title}
      initialRowsPerPage={INITIAL_ROWS_PER_PAGE}
      initialOrderByKey={'rentStart'}
      initialOrder={'desc'}
      rows={rows}
      searchEnabled={searchEnabled}
      tableHeadCells={tableHeadCells}
      rowLinkTo={(row) => generatePath(Routes.RENTAL, {id: row.id})}
    />
  )
}
